import React, { createContext, useContext, useLayoutEffect, useSyncExternalStore } from 'react';
import { useRouter } from 'next/router';
import { toOrganizationId, type OrganizationId } from './identity';
import {
  deriveClientUrlOrganizationFallback,
  deriveOrganization,
  getCommittedOrganization,
  getOrganizationGeneration,
  hasCommittedOrganizationIdentity,
  publishCommittedOrganization,
  subscribeOrganizationIdentity,
} from './organization';
import { SessionEndpointsProvider } from './SessionEndpointsContext';

// Named OrganizationIdentityContext (not OrganizationContext): the existing
// src/contexts/OrganizationContext.tsx carries organization metadata and
// features; this one carries only the identity for the mounted scope.
const OrganizationIdentityReactContext = createContext<OrganizationId | null>(null);

// Nullable route identity, safe at router-not-ready and SSR boundaries.
// Returns null on non-organization routes and before the router is ready; no
// tenant-scoped request may be dispatched from a null state.
export const useRouteOrganization = (): string | null => {
  const router = useRouter();
  if (!router.isReady) return null;
  return deriveOrganization(router.pathname, router.asPath);
};

// Required identity inside the committed organization scope. Throwing here is
// a development-time error (a component consuming tenant identity was mounted
// outside OrganizationScope), never a recoverable runtime state.
export const useCommittedOrganization = (): OrganizationId => {
  const value = useContext(OrganizationIdentityReactContext);
  if (value === null) {
    throw new Error('useCommittedOrganization must be used inside OrganizationScope on an organization route');
  }
  return value;
};

export const OrganizationScope = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter();
  const requestedOrganization = useRouteOrganization();
  useSyncExternalStore(subscribeOrganizationIdentity, getOrganizationGeneration, getOrganizationGeneration);
  const committedOrganization = getCommittedOrganization();
  // Only a genuine switch blocks children: an organization is already
  // committed and the route now asks for a different one, so rendering would
  // let the new subtree read the previous identity. First entry must render
  // instead of bailing - nothing is committed yet, the client resolves the
  // URL fallback, and the server never commits at all, so blocking there
  // would blank every organization page's server-rendered HTML and mismatch
  // on hydration.
  const isSwitchingOrganization = router.isReady && hasCommittedOrganizationIdentity() && requestedOrganization !== committedOrganization;

  // Publishing from a commit-phase effect rather than during render keeps an
  // abandoned or discarded transition from latching module identity onto an
  // organization whose UI never committed. Child passive effects (the entry
  // guard, feature providers) run after this layout effect, so they always
  // observe the committed value for their own mount.
  useLayoutEffect(() => {
    if (!router.isReady) return;
    publishCommittedOrganization(requestedOrganization);
  }, [committedOrganization, requestedOrganization, router.isReady]);

  if (isSwitchingOrganization) return null;

  // Before the router is ready nothing is committed; the requested
  // organization (or, on a cold client load, the URL fallback) backfills so
  // the scope key is stable across the router becoming ready - flipping the
  // key there would remount the whole organization subtree on every cold
  // load. On the server both fallbacks resolve to null by design.
  const preReadyFallback = !router.isReady && !hasCommittedOrganizationIdentity() ? deriveClientUrlOrganizationFallback(router.pathname) : null;
  const scopeOrganization = committedOrganization ?? requestedOrganization ?? preReadyFallback;
  const scopeKey = scopeOrganization ?? '__no-org__';
  const organizationId = toOrganizationId(scopeOrganization);
  return (
    <OrganizationIdentityReactContext.Provider key={scopeKey} value={organizationId}>
      <SessionEndpointsProvider organization={organizationId}>{children}</SessionEndpointsProvider>
    </OrganizationIdentityReactContext.Provider>
  );
};
